import Api from './Api';
import { useEffect, useState } from 'react';

/**
 * Corporate plan images are stored as opaque Firebase paths (`fpt1.…`), not as
 * URLs — they cannot be rendered directly. `POST firebase/signed-url` exchanges
 * one for a URL that works for 15 minutes.
 *
 * Resolve a path only where an image is actually painted, never in the action
 * that loads a list: a list screen would fire one request per row on every
 * keystroke of its search box, for images it does not even show. Keeping the
 * signed URL out of component state also matters because those same objects
 * are posted back on save — storing a signed URL there would persist it.
 */

// Refreshed a minute early so a URL handed to an <img> is never about to expire.
const TTL = 14 * 60 * 1000;
const cache = new Map();
const inFlight = new Map();

const isUrl = (value) => typeof value === 'string' && value.startsWith('http');

/** Exchange a stored file path for a signed URL. Returns '' when unavailable. */
export const getSignedUrl = async (filePath) => {
  if (!filePath || typeof filePath !== 'string') return '';
  if (isUrl(filePath)) return filePath;

  const cached = cache.get(filePath);
  if (cached && cached.expiresAt > Date.now()) return cached.url;

  // Two tiles can ask for the same file at once; share the one request.
  if (inFlight.has(filePath)) return inFlight.get(filePath);

  // showError = false: an image that cannot be resolved should not raise a
  // toast, and a screen full of them would raise one per image.
  const request = Api.post(
    'firebase/signed-url',
    { filePath },
    undefined,
    undefined,
    false
  )
    .then(({ result, status }) => {
      const signedUrl = status === 200 ? result?.signedUrl || '' : '';
      if (signedUrl)
        cache.set(filePath, { url: signedUrl, expiresAt: Date.now() + TTL });
      return signedUrl;
    })
    .finally(() => inFlight.delete(filePath));

  inFlight.set(filePath, request);
  return request;
};

/**
 * Resolve a stored path for rendering. Returns the value unchanged when there
 * is nothing to resolve — an empty field, an already-signed URL, or a File the
 * user has just picked and not yet uploaded.
 */
export const useSignedUrl = (value) => {
  const [resolved, setResolved] = useState(() =>
    typeof value === 'string' && !isUrl(value) ? '' : value
  );

  useEffect(() => {
    if (!value || typeof value !== 'string' || isUrl(value)) {
      setResolved(value);
      return undefined;
    }

    let alive = true;
    getSignedUrl(value).then((url) => alive && setResolved(url));
    return () => {
      alive = false;
    };
  }, [value]);

  return resolved;
};
